import { createContext, useCallback, useContext, useEffect, useSyncExternalStore } from 'react';

import { createPollCycleStore, type PollCycle, type PollCycleStore } from './pollCycle.js';

import type { PollSync } from '@mixwire/contracts';

export const CUE_STORAGE_KEY = 'mixwire-poll-cue';

export type PollCycleValue = {
  readonly store: PollCycleStore;
  /** The cue can be turned off entirely — see `PollCycleProvider`. */
  readonly isCueEnabled: boolean;
  readonly setCueEnabled: (isEnabled: boolean) => void;
};

/**
 * The default is a working store rather than `undefined`, so a component tree
 * rendered without the provider — a page under test, say — still gets the one
 * shared cycle instead of throwing. What the provider adds on top is the
 * on/off preference, which is the only part that needs React state.
 */
export const PollCycleContext = createContext<PollCycleValue>({
  store: createPollCycleStore(),
  isCueEnabled: true,
  setCueEnabled: (): void => undefined,
});

/**
 * Where the app is between polls. `isActive` false subscribes to nothing, which
 * is what stops the shared 100ms ticker running for a surface that has already
 * decided not to animate — a reduced-motion viewer, or the cue turned off.
 */
export const usePollCycle = (isActive: boolean): PollCycle => {
  const { store } = useContext(PollCycleContext);

  const subscribe = useCallback(
    (onChange: () => void): (() => void) =>
      isActive ? store.subscribe(onChange) : (): void => undefined,
    [store, isActive],
  );

  return useSyncExternalStore(subscribe, store.getSnapshot, store.getSnapshot);
};

export const useCueSetting = (): Omit<PollCycleValue, 'store'> => {
  const { isCueEnabled, setCueEnabled } = useContext(PollCycleContext);
  return { isCueEnabled, setCueEnabled };
};

/**
 * Feeds the shared cycle from whichever query drives this page. `updatedAtMs`
 * is the query's own record of when data last *arrived*, so the cycle restarts
 * on resolution rather than on the request going out — and a failed refetch
 * leaves it where it was, which is what eventually reads as stale.
 */
export const useReportPoll = (sync: PollSync | undefined, updatedAtMs: number): void => {
  const { store } = useContext(PollCycleContext);

  useEffect(() => {
    if (sync === undefined || updatedAtMs === 0) {
      return;
    }
    store.report(sync, updatedAtMs);
  }, [store, sync, updatedAtMs]);
};
